import { type JSX, useEffect, useId, useMemo, useRef, useState } from "react";

interface MultiSelectProps {
  label: string;
  options: string[];
  /** Chosen values. Empty means "all", which is how the query is phrased server-side. */
  selected: string[];
  onChange: (selected: string[]) => void;
  /** What to call the empty selection, e.g. "All projects". */
  allLabel: string;
  /** Shown in the search box. */
  searchPlaceholder?: string;
  disabled?: boolean;
}

/**
 * A dropdown for picking several of something, with a search box.
 *
 * A row of checkboxes was fine for three projects and stops being fine at ten: it grows sideways
 * across the filter row, pushes everything else out of line, and has no way to find one entry
 * among many. This keeps the filter row a fixed size whatever the list does.
 *
 * Empty means all, deliberately: it matches how the query is phrased -- no project filter is the
 * same request as every project selected -- so the two cannot drift apart.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel,
  searchPlaceholder = "Search…",
  disabled = false,
}: MultiSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const listId = useId();

  // Close on a click anywhere else, and on Escape. Both are what a dropdown is expected to do,
  // and without them this one stays open behind whatever the tester does next.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Focus the search on open: the reason for opening it is almost always to look for something.
  useEffect(() => {
    if (open) {
      search.current?.focus();
    } else {
      setQuery("");
    }
  }, [open]);

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? options.filter((option) => option.toLowerCase().includes(needle)) : options;
  }, [options, query]);

  const isChosen = (option: string): boolean =>
    selected.length === 0 || selected.includes(option);

  const toggle = (option: string): void => {
    // An empty selection means "all", so the first change has to start from everything rather
    // than from nothing -- otherwise unticking one entry would look like selecting one.
    const base = selected.length === 0 ? options : selected;
    const next = base.includes(option)
      ? base.filter((entry) => entry !== option)
      : [...new Set([...base, option])];
    onChange(next.length === options.length ? [] : next);
  };

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? selected[0]!
        : `${selected.length} of ${options.length}`;

  return (
    <div className="multiselect" ref={root}>
      <span className="multiselect__label">{label}</span>
      <button
        type="button"
        className="multiselect__control"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled || options.length === 0}
        onClick={() => setOpen(!open)}
      >
        <span className="multiselect__summary">{summary}</span>
        <span className="multiselect__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="multiselect__menu" id={listId}>
          <input
            ref={search}
            type="search"
            className="multiselect__search"
            value={query}
            placeholder={searchPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
          />

          <div className="multiselect__actions">
            <button
              type="button"
              className="link-button"
              onClick={() => onChange([])}
              disabled={selected.length === 0}
            >
              Select all
            </button>
            <button
              type="button"
              className="link-button"
              // One entry rather than none: an empty list means "all" here, so "clear" has to
              // leave something behind or it would select everything.
              onClick={() => onChange(matching[0] ? [matching[0]] : [])}
              disabled={options.length === 0}
            >
              Clear
            </button>
          </div>

          <ul className="multiselect__options">
            {matching.map((option) => (
              <li key={option}>
                <label className="multiselect__option">
                  <input
                    type="checkbox"
                    checked={isChosen(option)}
                    onChange={() => toggle(option)}
                  />
                  <span>{option}</span>
                </label>
              </li>
            ))}
            {matching.length === 0 && (
              <li className="multiselect__empty muted">Nothing matches “{query}”.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
